import { DateInput } from "@/components/DateInput";
import { useEffect, useRef, useState } from "react";
import { CheckCircle2, FileSpreadsheet, Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";
import { coverage, ERP_TYPES, STATUS_CLS, usePolledUpload } from "@/lib/erp";
import { cn } from "@/lib/utils";

const EXPECTED = { expenses: "Expected columns: FS Group, Sub Fs Group, Ac, DES, then month columns (MON-YY, e.g. OCT-26). A Total column, if present, is ignored." };

const Stat = ({ label, value, testid }) => (
  <div><div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div><div className="num text-sm font-semibold text-slate-900" data-testid={testid}>{value}</div></div>
);

export const ErrorList = ({ errors = [], total, testid }) => (
  errors.length > 0 && (
    <ul className="max-h-40 overflow-y-auto rounded-md border border-red-200 bg-red-50/60 px-3 py-2 text-xs text-red-700" data-testid={testid}>
      {errors.map((e, i) => <li key={i} className="py-0.5">{e}</li>)}
      {total > errors.length && <li className="pt-1 font-medium">+{total - errors.length} more</li>}
    </ul>
  )
);

const Progress = ({ job }) => {
  const p = job.progress;
  const pct = p?.total ? Math.min(100, Math.round((p.done / p.total) * 100)) : null;
  return (
    <div className="space-y-2" data-testid={`erp-progress-${job.type}`}>
      <div className="flex items-center gap-2 text-sm text-slate-700"><Loader2 className="h-4 w-4 animate-spin text-teal-600" />{job.phase}{pct != null && job.status === "Validating" ? ` · ${pct}%` : ""}</div>
      <div className="h-1.5 overflow-hidden rounded bg-slate-100"><div className="h-full bg-teal-600 transition-all" style={{ width: `${job.status === "Validating" ? pct ?? 5 : 100}%` }} /></div>
    </div>
  );
};

const Summary = ({ job }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-3 gap-3">
      <Stat label="Rows found" value={job.rows_found?.toLocaleString()} testid={`erp-rows-${job.type}`} />
      <Stat label="Valid" value={job.valid?.toLocaleString()} testid={`erp-valid-${job.type}`} />
      <Stat label="Invalid" value={job.invalid?.toLocaleString()} testid={`erp-invalid-${job.type}`} />
    </div>
    <div className="text-xs text-slate-600" data-testid={`erp-coverage-${job.type}`}>Covers: {coverage(job)}</div>
    <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid={`erp-replace-${job.type}`}>
      Will replace {job.to_replace?.toLocaleString()} existing records for these {job.type === "sales" ? "dates" : job.type === "expenses" ? "months" : "as-of date"}. Other dates are untouched.
    </div>
    {job.warnings?.map((w) => <p key={w} className="text-xs text-amber-700" data-testid={`erp-warning-${job.type}`}>{w}</p>)}
    <ErrorList errors={job.errors?.slice(0, 50)} total={job.error_count} testid={`erp-errors-${job.type}`} />
  </div>
);

export const UploadCard = ({ kind, onDone }) => {
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [asOf, setAsOf] = useState(new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Muscat" }));
  const [id, setId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [job, markImporting] = usePolledUpload(id);
  const status = job?.status;

  useEffect(() => { if (["Success", "Partial"].includes(status)) onDone?.(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    if (kind === "outstanding") fd.append("as_of", asOf);
    try {
      const { data } = await api.post(`/erp/${kind}/upload`, fd);
      setId(data.id);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const confirm = async () => {
    try { await api.post(`/erp/uploads/${id}/confirm`); markImporting(); } catch (e) { toast.error(apiError(e)); }
  };
  const reset = async (cancel) => {
    if (cancel) await api.post(`/erp/uploads/${id}/cancel`).catch(() => {});
    setId(null); setFile(null);
  };
  const working = status && !["Ready", "Failed", "Success", "Partial", "Cancelled"].includes(status);

  return (
    <section className="rise-in flex flex-col rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid={`erp-card-${kind}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-teal-600" /><h2 className="text-base font-semibold text-slate-900 md:text-lg">{ERP_TYPES[kind]}</h2></div>
        {status && <span className={cn("rounded px-2 py-0.5 text-[11px] font-semibold", STATUS_CLS[status])} data-testid={`erp-status-${kind}`}>{status}</span>}
      </div>
      <div className="mt-4 flex-1 space-y-4">
        {!id && (
          <>
            {kind === "outstanding" && (
              <div className="space-y-1.5"><Label htmlFor="as-of" className="text-xs">As of date</Label>
                <DateInput id="as-of" value={asOf} onChange={setAsOf} className="w-44" testid="erp-asof-input" /></div>
            )}
            <button type="button" onClick={() => input.current?.click()} className="flex w-full items-center justify-center gap-2 rounded-md border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-600 transition-colors hover:border-teal-400" data-testid={`erp-pick-${kind}`}>
              <Upload className="h-4 w-4" /> {file ? file.name : `Choose ${ERP_TYPES[kind]} report (.xlsx)`}
            </button>
            {EXPECTED[kind] && <p className="text-xs text-slate-500" data-testid={`erp-expected-${kind}`}>{EXPECTED[kind]}</p>}
            <input ref={input} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid={`erp-file-${kind}`} />
            <Button disabled={!file || busy} onClick={start} className="w-full bg-teal-600 text-white hover:bg-teal-700" data-testid={`erp-upload-${kind}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload & validate"}
            </Button>
          </>
        )}
        {job && working && <Progress job={job} />}
        {job && status === "Ready" && <Summary job={job} />}
        {job && status === "Failed" && <ErrorList errors={job.errors} total={job.error_count} testid={`erp-errors-${kind}`} />}
        {job && ["Success", "Partial"].includes(status) && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-md bg-green-50 px-3 py-2 text-sm font-medium text-green-800" data-testid={`erp-result-${kind}`}>
              <CheckCircle2 className="h-4 w-4" /> Imported {job.imported?.toLocaleString()} · replaced {job.replaced?.toLocaleString()} · skipped {job.skipped?.toLocaleString()}
            </div>
            <div className="text-xs text-slate-600">Covers: {coverage(job)}</div>
            <ErrorList errors={job.errors?.slice(0, 50)} total={job.error_count} testid={`erp-errors-${kind}`} />
          </div>
        )}
      </div>
      {job && status === "Ready" && (
        <div className="mt-4 flex gap-2">
          <Button variant="outline" onClick={() => reset(true)} data-testid={`erp-cancel-${kind}`}><X className="mr-1 h-4 w-4" />Cancel</Button>
          <Button disabled={!job.valid} onClick={confirm} className="flex-1 bg-teal-600 text-white hover:bg-teal-700" data-testid={`erp-confirm-${kind}`}>Confirm import</Button>
        </div>
      )}
      {job && ["Failed", "Success", "Partial"].includes(status) && (
        <Button variant="outline" className="mt-4" onClick={() => reset(false)} data-testid={`erp-again-${kind}`}>Upload another file</Button>
      )}
    </section>
  );
};
